import React, { useEffect, useRef, useState } from "react";
import { Item, ComboBox } from "@adobe/react-spectrum";
import { useAsyncList } from "@react-stately/data";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import { useTranslation } from "react-i18next";
import { ListItemTypeRequest } from "../../../../../services/soap/item/requests/ListItemTypeRequest";

interface itemType {
  // keep your full interface as-is
  id: number;
  name: string;
  showChild: string;
  showParent: string;
  treeHandle: string;
  measureStock: string;
  baseType: string;

  attrib0_filter: string;
  attrib1_filter: string;
  attrib2_filter: string;
  attrib3_filter: string;
  attrib4_filter: string;
  attrib5_filter: string;
  attrib6_filter: string;
  attrib7_filter: string;
  attrib8_filter: string;
  attrib9_filter: string;

  attrib0_name: string;
  attrib1_name: string;
  attrib2_name: string;
  attrib3_name: string;
  attrib4_name: string;
  attrib5_name: string;
  attrib6_name: string;
  attrib7_name: string;
  attrib8_name: string;
  attrib9_name: string;

  attrib0_priority: string;
  attrib1_priority: string;
  attrib2_priority: string;
  attrib3_priority: string;
  attrib4_priority: string;
  attrib5_priority: string;
  attrib6_priority: string;
  attrib7_priority: string;
  attrib8_priority: string;
  attrib9_priority: string;

  attrib0_type: string;
  attrib1_type: string;
  attrib2_type: string;
  attrib3_type: string;
  attrib4_type: string;
  attrib5_type: string;
  attrib6_type: string;
  attrib7_type: string;
  attrib8_type: string;
  attrib9_type: string;

  field0_filter: string;
  field1_filter: string;
  field2_filter: string;
  field3_filter: string;
  field4_filter: string;
  field5_filter: string;
  field6_filter: string;
  field7_filter: string;
  field8_filter: string;
  field9_filter: string;

  field0_name: string;
  field1_name: string;
  field2_name: string;
  field3_name: string;
  field4_name: string;
  field5_name: string;
  field6_name: string;
  field7_name: string;
  field8_name: string;
  field9_name: string;

  field0_priority: string;
  field1_priority: string;
  field2_priority: string;
  field3_priority: string;
  field4_priority: string;
  field5_priority: string;
  field6_priority: string;
  field7_priority: string;
  field8_priority: string;
  field9_priority: string;

  field0_type: string;
  field1_type: string;
  field2_type: string;
  field3_type: string;
  field4_type: string;
  field5_type: string;
  field6_type: string;
  field7_type: string;
  field8_type: string;
  field9_type: string;
}

interface IItemTypeDropDownProps {
  selectedType: string;
  isEdit?: boolean;
  onChange: (data: itemType | null) => void; // ✅ allow null on clear
  isRequired?: any;
  errorMessage?: any;
  validationState?: any;
  setProductType: (data: itemType | null) => void; // ✅ allow null
}

const ItemTypeDropDown: React.FC<IItemTypeDropDownProps> = ({
  selectedType,
  isEdit,
  onChange,
  isRequired,
  errorMessage,
  validationState,
  setProductType,
}) => {
  const { store, itemService } = useDependency();
  const { t } = useTranslation("products");
  const _t = (key: string) => t(`addNewProduct.${key}`, { ns: "products" });

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>("");

  const didInitialAutoSelectRef = useRef(false);
  const isUserEditingRef = useRef(false);

  const list = useAsyncList<itemType>({
    async load({ filterText }) {
      try {
        const request = new ListItemTypeRequest(store.Server, store.SessionId);
        const response = await itemService.listItemType(request);

        let items: any[] = response?.ITEM_TYPE || [];
        if (!Array.isArray(items)) return { items: [] };

        const ft = (filterText ?? "").trim().toLowerCase();
        if (ft) {
          items = items.filter((x) => (x.name ?? "").toLowerCase().includes(ft));
        }

        return { items };
      } catch (e) {
        console.error("Error fetching itemTypes:", e);
        return { items: [] };
      }
    },
  });

  useEffect(() => {
    list.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ initial load: auto-select 0th item ONCE (only if not edit)
  useEffect(() => {
    if (!didInitialAutoSelectRef.current && list.items.length > 0 && !isEdit) {
      didInitialAutoSelectRef.current = true;

      const first = list.items[0];
      setSelectedKey(String(first.id));
      setInputValue(first.name ?? "");
      onChange(first);
      setProductType(first);
    }
  }, [list.items, isEdit, onChange, setProductType]);

  // ✅ sync from parent ONLY when user not typing
  useEffect(() => {
    if (isUserEditingRef.current) return;

    if (!selectedType) {
      setSelectedKey(null);
      setInputValue("");
      return;
    }

    setSelectedKey(String(selectedType));
    const selected = list.items.find((x) => String(x.id) === String(selectedType));
    setInputValue(selected?.name ?? "");
  }, [selectedType, list.items]);

  const handleInputChange = (val: string) => {
    isUserEditingRef.current = true;

    setInputValue(val);
    list.setFilterText(val);

    if (selectedKey) {
      const selected = list.items.find((x) => String(x.id) === String(selectedKey));
      const selectedLabel = selected?.name ?? "";
      if (val !== selectedLabel) setSelectedKey(null);
    }

    if (!val.trim()) {
      setSelectedKey(null);
      list.setFilterText("");
      onChange(null);
      setProductType(null);
    }
  };

  const handleSelectionChange = (key: React.Key | null) => {
    isUserEditingRef.current = false;

    const idStr = key ? String(key) : null;
    setSelectedKey(idStr);

    if (!idStr) return;

    const selected = list.items.find((x) => String(x.id) === idStr);
    if (selected) {
      setInputValue(selected.name ?? "");
      onChange(selected);
      setProductType(selected);
      list.setFilterText("");
    }
  };

  return (
    <ComboBox
      label={_t("type")}
      aria-label={_t("itemtype")}
      items={list.items}
      selectedKey={selectedKey}
      inputValue={inputValue}
      loadingState={list.loadingState}
      width={{ base: "100%", L: "50%" }}
      isDisabled={isEdit}
      isRequired={isRequired}
      errorMessage={errorMessage}
      validationState={validationState}
      onFocus={() => {
        isUserEditingRef.current = true;
        list.setFilterText("");
      }}
      onBlur={() => {
        isUserEditingRef.current = false;
      }}
      onInputChange={handleInputChange}
      onSelectionChange={handleSelectionChange}
    >
      {(item) => (
        <Item key={item.id} textValue={item.name}>
          {item.name}
        </Item>
      )}
    </ComboBox>
  );
};

export default ItemTypeDropDown;
